import BaseService from 'services/BaseService';
import {
  AssetPerformanceResponse,
  CoverageResponse,
  CreativePerformanceResponse,
  CreativeQuery,
  VariantsResponse,
} from 'pages/CreativePerformance/types';

// The API's ceiling. The page compares the row count against it and says so
// when a window is wide enough to be cut, rather than presenting a truncated
// set as if it were the whole thing.
export const MAX_ROWS = 2000;

class CreativePerformanceService extends BaseService {
  basePath = 'v1/creative-performance';

  async getAds(filters: CreativeQuery) {
    await this._getAccessToken();
    return this.handleHttpRequest<CreativePerformanceResponse>(
      () =>
        this.http.get(`${this.basePath}/ads`, {
          // Only the parameters that change which rows exist. Media type,
          // status, search, min spend and sort are applied in the browser, so
          // adjusting them costs no round trip.
          params: {
            start: filters.start,
            end: filters.end,
            groupBy: filters.groupBy,
            limit: MAX_ROWS,
          },
        }),
      'Failed to load creative performance',
    );
  }

  async getAssets(filters: CreativeQuery) {
    await this._getAccessToken();
    return this.handleHttpRequest<AssetPerformanceResponse>(
      () =>
        this.http.get(`${this.basePath}/assets`, {
          params: {
            start: filters.start,
            end: filters.end,
            assetType: filters.assetType,
            // Optional refinements (Ad Copy Studio): the endpoint supports them.
            minSpend: filters.minSpend,
            sort: filters.sort,
            direction: filters.direction,
            limit: filters.limit ?? MAX_ROWS,
          },
        }),
      'Failed to load asset performance',
    );
  }

  async getVariants(
    adId: string,
    assetType: 'headline' | 'primary_text',
    start: string,
    end: string,
  ) {
    await this._getAccessToken();
    return this.handleHttpRequest<VariantsResponse>(
      () =>
        this.http.get(`${this.basePath}/variants`, {
          params: { adId, assetType, start, end },
        }),
      'Failed to load variant performance',
    );
  }

  async getCoverage(start: string, end: string) {
    await this._getAccessToken();
    return this.handleHttpRequest<CoverageResponse>(
      () => this.http.get(`${this.basePath}/coverage`, { params: { start, end } }),
      'Failed to load creative performance coverage',
    );
  }
}

export default CreativePerformanceService;
